import {TprmTableColumn, TprmExternalColumnValue, TprmExternalTableRow, TprmTableRowId} from "platformed-client/types";
import {unreachableCase} from "../../../utils/typescript";

export const titleCase = (s: string) => (s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Human-readable rendering of a single cell value for the rows table. */
export function columnValueText(column: TprmTableColumn, value: TprmExternalColumnValue | undefined): string {
  if (value == null) {
    return "";
  }
  if (value.type === "Text") {
    return value.content.text;
  }
  if (value.type === "Files") {
    return `${value.content.file_ids.length} file(s)`;
  }
  if (column.answer_config.type === "Select") {
    const {options} = column.answer_config.content;
    return value.content.selected.map(v => options.find(o => o.value === v)?.label ?? v).join(", ");
  }
  return value.content.selected.join(", ");
}

/** True when a column cell has been given a non-empty value. */
export function isColumnFilled(value: TprmExternalColumnValue | undefined): boolean {
  if (value == null) {
    return false;
  }
  switch (value.type) {
    case "Text":
      return value.content.text.trim().length > 0;
    case "Select":
      return value.content.selected.length > 0;
    case "Files":
      return value.content.file_ids.length > 0;
    default:
      return unreachableCase(value);
  }
}

/** An empty value matching a column's type — used to adopt an ungrouped group. */
export function emptyColumnValue(column: TprmTableColumn): TprmExternalColumnValue {
  switch (column.answer_config.type) {
    case "Text":
      return {type: "Text", content: {text: ""}};
    case "Select":
      return {type: "Select", content: {selected: []}};
    case "Files":
      return {type: "Files", content: {file_ids: []}};
    default:
      return unreachableCase(column.answer_config);
  }
}

/**
 * Where `rowId` should be inserted so it joins the run of rows already carrying
 * `value` in the grouping column. Returns `null` when the row already holds that
 * group value and so needs no move. Indices count the array with `rowId` removed,
 * matching the move endpoint's remove-then-insert. A value no other row carries
 * yields the end index, landing the row in a fresh group.
 */
export function groupRunInsertionIndex(
  rows: TprmExternalTableRow[],
  rowId: TprmTableRowId,
  groupColumn: TprmTableColumn,
  value: TprmExternalColumnValue,
): number | null {
  const newText = columnValueText(groupColumn, value);
  const current = rows.find(r => r.id === rowId);
  if (current == null || columnValueText(groupColumn, current.values[groupColumn.key]) === newText) {
    return null;
  }
  const others = rows.filter(r => r.id !== rowId);
  const lastMatch = others.reduce(
    (found, row, index) => (columnValueText(groupColumn, row.values[groupColumn.key]) === newText ? index : found),
    -1,
  );
  return lastMatch >= 0 ? lastMatch + 1 : others.length;
}
